import { createElement } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus } from "lucide-react";

const HIDDEN_KEYS = ["key", "_id"];

const humanize = (k) =>
  k
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

function cloneTemplate(sample) {
  if (Array.isArray(sample)) return [];
  if (sample && typeof sample === "object") {
    const o = {};
    for (const k of Object.keys(sample)) o[k] = cloneTemplate(sample[k]);
    return o;
  }
  if (typeof sample === "number") return 0;
  if (typeof sample === "boolean") return false;
  return "";
}

// Render Field via createElement (NOT JSX) to avoid the dev visual-edits
// babel plugin infinitely tracing a self-referential <Field /> element.
const renderField = (props) => createElement(Field, { key: props.fieldKey, ...props });

function Field({ value, onChange, label, fieldKey }) {
  if (typeof value === "boolean") {
    return (
      <div className="flex items-center justify-between gap-3 py-1">
        <span className="text-sm text-slate-300">{label}</span>
        <Switch
          checked={value}
          onCheckedChange={onChange}
          data-testid={`field-${fieldKey}`}
        />
      </div>
    );
  }

  if (typeof value === "number") {
    return (
      <div className="space-y-1">
        {label && (
          <span className="block text-xs uppercase tracking-wide text-slate-400">
            {label}
          </span>
        )}
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={`field-${fieldKey}`}
        />
      </div>
    );
  }

  if (typeof value === "string") {
    const long = value.length > 55;
    return (
      <div className="space-y-1">
        {label && (
          <span className="block text-xs uppercase tracking-wide text-slate-400">
            {label}
          </span>
        )}
        {long ? (
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            data-testid={`field-${fieldKey}`}
          />
        ) : (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`field-${fieldKey}`}
          />
        )}
      </div>
    );
  }

  if (Array.isArray(value)) {
    const addItem = () => {
      const template = value.length ? cloneTemplate(value[0]) : "";
      onChange([...value, template]);
    };
    const removeItem = (i) => onChange(value.filter((_, idx) => idx !== i));
    const updateItem = (i, v) =>
      onChange(value.map((item, idx) => (idx === i ? v : item)));

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-amber-300">
            {label} <span className="text-slate-500">({value.length})</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItem}
            data-testid={`add-${fieldKey}`}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-3">
          {value.map((item, i) => (
            <div
              key={i}
              className="relative rounded-lg border border-white/10 bg-white/[0.03] p-4 pt-8"
            >
              <span className="absolute top-2 left-3 text-xs text-slate-500">
                #{i + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-1 right-1 text-red-400 hover:text-red-300"
                onClick={() => removeItem(i)}
                data-testid={`remove-${fieldKey}-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              {renderField({
                value: item,
                label: "",
                fieldKey: `${fieldKey}-${i}`,
                onChange: (v) => updateItem(i, v),
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (value && typeof value === "object") {
    const keys = Object.keys(value).filter((k) => !HIDDEN_KEYS.includes(k));
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3">
        {label && (
          <div className="text-sm font-semibold text-cyan-300">{label}</div>
        )}
        {keys.map((k) =>
          renderField({
            value: value[k],
            label: humanize(k),
            fieldKey: `${fieldKey}-${k}`,
            onChange: (v) => onChange({ ...value, [k]: v }),
          })
        )}
      </div>
    );
  }

  return null;
}

export default function ContentEditor({ content, onChange }) {
  const allKeys = Object.keys(content).filter((k) => !HIDDEN_KEYS.includes(k));
  const scalarKeys = allKeys.filter(
    (k) => typeof content[k] !== "object" || content[k] === null
  );
  const complexKeys = allKeys.filter(
    (k) => typeof content[k] === "object" && content[k] !== null
  );

  const update = (key, v) => onChange({ ...content, [key]: v });

  return (
    <div className="space-y-6">
      {scalarKeys.length > 0 && (
        <section
          className="rounded-xl border border-white/10 bg-[#0B1220] p-5 space-y-4"
          data-testid="section-general"
        >
          <h3 className="font-display text-lg uppercase tracking-tight text-white">
            General
          </h3>
          {scalarKeys.map((k) =>
            renderField({
              value: content[k],
              label: humanize(k),
              fieldKey: `root-${k}`,
              onChange: (v) => update(k, v),
            })
          )}
        </section>
      )}

      {complexKeys.map((k) => (
        <section
          key={k}
          className="rounded-xl border border-white/10 bg-[#0B1220] p-5 space-y-4"
          data-testid={`section-${k}`}
        >
          <h3 className="font-display text-lg uppercase tracking-tight text-white">
            {humanize(k)}
          </h3>
          {renderField({
            value: content[k],
            label: "",
            fieldKey: `root-${k}`,
            onChange: (v) => update(k, v),
          })}
        </section>
      ))}
    </div>
  );
}
